import { Button as ButtonType } from '@bw/apis/contentful';
import { Image } from '@bw/elements';
import {
	documentToReactComponents,
	Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { Asset } from 'contentful';
import { ButtonWrapper } from '../../components/button/ButtonWrapper';
import { BlockKey } from '../../factories/types';
import styles from './RichText.module.scss';

type RichTextProps = {
	document?: Document;
};

/**
 * Renders rich text data using common options, such as singleLineMode
 */
export const RichText = ({ document }: RichTextProps) =>
	hasContent(document) ? documentToReactComponents(document, options) : null;

const hasContent = (document?: Document): document is Document => {
	return !!document?.content.some((node) => node.content);
};

const options: Options = {
	preserveWhitespace: true,
	renderNode: {
		/**
		 * NOTE: this one fixes empty paragraphs that are sometimes added after completely deleting the content of the field.
		 */
		[BLOCKS.PARAGRAPH]: (_, children) => {
			if (Array.isArray(children) && children.length === 1) {
				if (!children[0]) return null;
			}

			return <p>{children}</p>;
		},

		[INLINES.EMBEDDED_ENTRY]: (node) => {
			const type: BlockKey = node.data?.target?.sys?.contentType?.sys?.id;

			switch (type) {
				case 'button': {
					const data: ButtonType = node.data?.target;

					return <ButtonWrapper data={data} buttonStyle="hyperlink" />;
				}
				default: {
					console.warn('Unexpected inline entry.');
					return null;
				}
			}
		},

		[BLOCKS.HEADING_1]: (_, children) => {
			if (Array.isArray(children) && children.length === 1) {
				if (!children[0]) return null;
			}

			return <p className={styles.heading}>{children}</p>;
		},

		[BLOCKS.EMBEDDED_ASSET]: ({ data: { target } }) => {
			const {
				fields: { title, description, file },
			} = target as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>;

			if (!file) {
				return null;
			}

			const {
				url,
				details: { image },
				contentType,
			} = file;

			switch (contentType) {
				case 'image/jpeg':
				case 'image/png': {
					return (
						<Image
							src={url}
							alt={description || ''}
							width={image?.width || 100}
							height={image?.height || 100}
						/>
					);
				}
				default: {
					return null;
				}
			}
		},
	},
};
